/**
 * MeetingSummariesPage — Grid view of all meeting summaries in the org.
 *
 * Fetches ahoy_meeting_notes objects and renders them as compact summary
 * cards in a responsive grid, showing meeting title, date/time, duration,
 * source provider, and attendee avatars.
 *
 * @deprecated Slated for removal — superseded by the meetings CRM list
 * surface. Deliberately excluded from the URL-synced list-state work
 * (?filters=/?sort=/?search= params and the explicit saved-view save flow);
 * do not extend this page.
 */

import { useQuery } from '@tanstack/react-query';
import { File06 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { useTitle } from '@/hooks/useTitle';
import { EmptyState } from '@/lib/base/empty-state';
import { FormProvider } from '@/lib/base/forms/form-context';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { PageLayout } from '@/lib/base/page-layout/page-layout';
import {
  ToolbarFilter,
  ToolbarSearch,
  ToolbarSection,
} from '@/lib/base/page-layout/toolbar';
import { useSearchInput } from '@/lib/base/page-layout/use-search-input';
import SchemaFilterBuilder, {
  hasIncompleteFilterConditions,
} from '@/lib/crm/components/SchemaFilterBuilder';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import type { FilterCondition } from '@/lib/crm/filters/types';
import type { FilterConditionGroup } from '@/lib/crm/filters/types';
import { useActiveListView } from '@/lib/crm/hooks/useActiveListView';
import { useMeetingNoteOwners } from '@/lib/crm/hooks/useMeetingNoteOwners';
import type { SavedViewSurface } from '@/lib/crm/hooks/useSavedViews';
import { useSavedViews } from '@/lib/crm/hooks/useSavedViews';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { SaveCurrentViewModal } from '@/lib/crm/list/components/SaveCurrentViewModal';
import { SavedViewTabs } from '@/lib/crm/list/components/SavedViewTabs';
import { MEETING_NOTES_TO_OWNER_ASSOCIATION } from '@/lib/crm/utils/meetingNotes';
import type { MeetingSummaryItem } from '@/lib/crm/utils/meetingSeriesGrouping';
import { groupMeetingSummaries } from '@/lib/crm/utils/meetingSeriesGrouping';
import { useViewHealthMarker } from '@/lib/view-health';
import type {
  CrmObject,
  CrmObjectsListResponse,
  FilterGroup,
} from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { convertConditionGroupsToFilterGroups } from '@/utils/filterUtils';

import { MeetingSeriesCard } from './MeetingSeriesCard';
import { MeetingSummaryCard } from './MeetingSummaryCard';

// ============================================================================
// Static config
// ============================================================================

const EMPTY_ICON = (
  <FeaturedIcon icon={File06} size='md' theme='light-outline' color='gray' />
);

// The filter builder renders into groups; an empty filter state is a single
// empty group.
const EMPTY_BUILDER_GROUPS: FilterConditionGroup[] = [
  { id: 'group-0', conditions: [] },
];

// ============================================================================
// Data fetching
// ============================================================================

function useMeetingSummaries(
  orgId: number | undefined,
  filterGroups: FilterGroup[]
) {
  return useQuery({
    queryKey: ['meetingSummaries', orgId, filterGroups],
    queryFn: async () => {
      if (!orgId) {
        return {
          results: [],
          count: 0,
          next: null,
          previous: null,
        } as CrmObjectsListResponse;
      }
      const client = createCRMObjectClient(orgId, 'ahoy_meeting_notes');
      return client.list(
        {
          properties: ['ahoy_title', 'ahoy_notes_source', 'ahoy_created_at'],
          order_by: '-ahoy_created_at',
          ...(filterGroups.length > 0 ? { filter_groups: filterGroups } : {}),
        },
        {
          include_associations: {
            ahoy_meeting_notes_to_meeting: {
              properties: [
                'ahoy_title',
                'ahoy_start_time',
                'ahoy_end_time',
                'ahoy_invitations',
                'ahoy_recurring_series_id',
              ],
            },
            [MEETING_NOTES_TO_OWNER_ASSOCIATION]: { properties: [] },
          },
        }
      );
    },
    enabled: !!orgId,
    staleTime: 2 * 60_000,
  });
}

// ============================================================================
// Loading skeleton
// ============================================================================

const SKELETON_ITEMS = [1, 2, 3, 4, 5, 6];

function GridSkeleton() {
  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
      {SKELETON_ITEMS.map(i => (
        <div
          key={i}
          className='rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] p-4 animate-pulse'
        >
          <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-3/4 mb-3' />
          <div className='h-3 bg-[var(--color-bg-tertiary)] rounded w-full mb-4' />
          <div className='flex items-center gap-2'>
            <div className='h-4 w-4 bg-[var(--color-bg-tertiary)] rounded' />
            <div className='h-3 bg-[var(--color-bg-tertiary)] rounded w-20' />
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

export default function MeetingSummariesPage() {
  useTitle('Meeting Summaries');
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const navigate = useNavigate();
  const { openSlideout } = useSlideoutStack();
  const { user } = useAuthInfo();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const { searchTerm, inputRef, handleSearchChange, handleSearchBlur } =
    useSearchInput({ searchValue: searchQuery, onSearch: setSearchQuery });

  // Filter state — group-native (AND within a group, OR between groups) so an
  // applied saved view keeps its OR structure through edit → save → reload.
  const [filterConditionGroups, setFilterConditionGroups] = useState<
    FilterConditionGroup[]
  >([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filtersAreValid, setFiltersAreValid] = useState(true);

  // Flat read across all groups — used only for validity/completeness checks
  // and the active-filter count, never for building the backend query.
  const filterConditions = useMemo<FilterCondition[]>(
    () => filterConditionGroups.flatMap(g => g.conditions),
    [filterConditionGroups]
  );

  const filterFieldConfig = useMemo(
    () => ({
      filters: {
        validate: (val: unknown) => {
          const { conditions, valid } = val as {
            conditions: FilterCondition[];
            valid: boolean;
          };
          if (!valid) {
            return 'Some filter values are invalid';
          }
          if (hasIncompleteFilterConditions(conditions)) {
            return 'Some filter conditions are incomplete';
          }
          return undefined;
        },
      },
    }),
    []
  );

  const filterFormValues = useMemo(
    () => ({
      filters: { conditions: filterConditions, valid: filtersAreValid },
    }),
    [filterConditions, filtersAreValid]
  );

  // The builder always needs at least one group to render into; an empty
  // filter state is presented as a single empty group.
  const builderGroups = useMemo<FilterConditionGroup[]>(
    () =>
      filterConditionGroups.length > 0
        ? filterConditionGroups
        : EMPTY_BUILDER_GROUPS,
    [filterConditionGroups]
  );

  const handleGroupsChange = useCallback(
    (next: FilterConditionGroup[]) => setFilterConditionGroups(next),
    []
  );

  const [appliedFilterGroups, setAppliedFilterGroups] = useState<FilterGroup[]>(
    []
  );

  const { data, isLoading, error, refetch } = useMeetingSummaries(
    orgId,
    appliedFilterGroups
  );

  const handleApplyFilters = useCallback(
    (groups?: FilterConditionGroup[]) => {
      const toApply = groups ?? filterConditionGroups;
      const { filter_groups } = convertConditionGroupsToFilterGroups(toApply);
      setAppliedFilterGroups(filter_groups);
    },
    [filterConditionGroups]
  );

  // Saved views surface — filter-only (no captureViewState → auto-save disabled,
  // empty view_state stored on save). applyFilterConditionGroups passes groups
  // explicitly so there is no stale-closure read of the draft groups.
  const getFilterConditionGroups = useCallback(
    (): FilterConditionGroup[] =>
      filterConditionGroups.filter(g => g.conditions.length > 0),
    [filterConditionGroups]
  );

  // Group-native apply: the groups are stored and queried as-is, so a saved
  // view's OR structure is preserved on this surface (both in the results and
  // in what a subsequent save persists).
  const applyFilterConditionGroups = useCallback(
    (groups: FilterConditionGroup[]) => {
      setFilterConditionGroups(groups);
      handleApplyFilters(groups);
    },
    [handleApplyFilters]
  );

  // Group-native auto-apply: the builder hands back the full groups on
  // removal, so apply them as-is.
  const handleAutoApplyGroups = useCallback(
    (groups: FilterConditionGroup[]) => handleApplyFilters(groups),
    [handleApplyFilters]
  );

  const savedViewSurface = useMemo<SavedViewSurface>(
    () => ({
      targetEntityType: 'ahoy_meeting_notes',
      getFilterConditionGroups,
      applyFilterConditionGroups,
    }),
    [getFilterConditionGroups, applyFilterConditionGroups]
  );

  // No built-in views here: useActiveListView(undefined) owns the single
  // ?savedView identity (saved view uuid or null "All"), as useSavedViews did
  // internally before the unification.
  const savedViewActiveView = useActiveListView(undefined);
  const {
    activeViewId,
    applyView,
    selectAll,
    saveModalOpen,
    savePayload,
    openSaveModal,
    closeSaveModal,
    onViewSaved,
    onViewDeleted,
  } = useSavedViews(savedViewSurface, savedViewActiveView);

  const handleSaveModalOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        closeSaveModal();
      }
    },
    [closeSaveModal]
  );

  // SchemaFilterBuilder's onApply hands back a flat condition list purely as an
  // Enter-key trigger; the authoritative draft is the group state the builder
  // already pushed through onChange, so apply that.
  const handleApplyFiltersAndClose = useCallback(() => {
    if (!filtersAreValid || hasIncompleteFilterConditions(filterConditions)) {
      return;
    }
    handleApplyFilters();
    setShowFilters(false);
  }, [handleApplyFilters, filterConditions, filtersAreValid]);

  const handleToggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  const handleCloseFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  const handleApplyFiltersClick = useCallback(() => {
    handleApplyFiltersAndClose();
  }, [handleApplyFiltersAndClose]);

  const notes = useMemo(() => data?.results ?? [], [data?.results]);

  const ownerByNote = useMeetingNoteOwners(notes);

  useViewHealthMarker('meeting-summaries-ready', {
    ready: !isLoading && !error && notes.length > 0,
  });
  useViewHealthMarker('meeting-summaries-empty-ready', {
    ready: !isLoading && !error && notes.length === 0,
  });

  const associatedObjects = useMemo(
    () => data?.associated_objects ?? {},
    [data?.associated_objects]
  );

  const filteredNotes = useMemo(() => {
    if (!searchQuery) {
      return notes;
    }
    const q = searchQuery.toLowerCase();
    return notes.filter(note => {
      const title = getPropertyValue<string>(note.properties?.ahoy_title) ?? '';
      return title.toLowerCase().includes(q);
    });
  }, [notes, searchQuery]);

  const groupedItems = useMemo<MeetingSummaryItem[]>(
    () => groupMeetingSummaries(filteredNotes, associatedObjects),
    [filteredNotes, associatedObjects]
  );

  const handleCardClick = useCallback(
    (note: CrmObject) => {
      // Full-page view is always on now (flag removed); fall back to the
      // slideout only when we have no org id to build the route.
      if (orgId) {
        navigate(`/${orgId}/meeting-summaries/${note.entity_id}`);
      } else {
        openSlideout(note.entity_id);
      }
    },
    [orgId, navigate, openSlideout]
  );

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const activeFilterCount = useMemo(
    () =>
      filterConditions.filter(c => {
        const needsValue = !['is_empty', 'is_not_empty'].includes(c.operator);
        if (!needsValue) {
          return true;
        }
        if (c.value === null || c.value === undefined || c.value === '') {
          return false;
        }
        if (Array.isArray(c.value) && c.value.length === 0) {
          return false;
        }
        return true;
      }).length,
    [filterConditions]
  );

  const subtitle = useMemo(() => {
    const count = data?.count ?? 0;
    return `${count.toLocaleString()} ${count === 1 ? 'summary' : 'summaries'}`;
  }, [data?.count]);

  const toolbarContent = useMemo(
    () => (
      <ToolbarSection>
        <ToolbarSearch
          value={searchTerm}
          onChange={handleSearchChange}
          inputRef={inputRef}
          onBlur={handleSearchBlur}
          placeholder='Search meeting summaries'
        />
        <ToolbarFilter
          activeCount={activeFilterCount}
          onClick={handleToggleFilters}
        />
      </ToolbarSection>
    ),
    [
      searchTerm,
      handleSearchChange,
      inputRef,
      handleSearchBlur,
      activeFilterCount,
      handleToggleFilters,
    ]
  );

  return (
    <PageLayout
      title='Meeting Summaries'
      subtitle={subtitle}
      toolbar={toolbarContent}
      titleTrailing={
        currentOrg && user ? (
          <SavedViewTabs
            orgId={currentOrg.id}
            entityType='ahoy_meeting_notes'
            activeViewId={activeViewId}
            onSelectView={applyView}
            onSelectAll={selectAll}
            onSaveCurrentView={openSaveModal}
            onViewDeleted={onViewDeleted}
          />
        ) : undefined
      }
    >
      <div className='flex-1 overflow-y-auto px-4 py-4'>
        {isLoading && <GridSkeleton />}

        {error && !isLoading && (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <p className='text-sm text-[var(--color-text-error-primary)] mb-3'>
              We couldn't load your meeting summaries.
            </p>
            <button
              type='button'
              onClick={handleRetry}
              className='text-sm text-fg-secondary underline hover:no-underline cursor-pointer bg-transparent border-none p-0'
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !error && notes.length === 0 && (
          <EmptyState
            variant='empty'
            title='No meeting summaries yet'
            description='Meeting summaries will appear here when transcripts are captured from your meetings.'
            icon={EMPTY_ICON}
            size='sm'
          />
        )}

        {!isLoading &&
          !error &&
          notes.length > 0 &&
          filteredNotes.length === 0 && (
            <EmptyState
              variant='empty'
              title='No matching summaries'
              description='Try a different search term.'
              icon={EMPTY_ICON}
              size='sm'
            />
          )}

        {!isLoading && !error && filteredNotes.length > 0 && (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
            {groupedItems.map(item =>
              item.kind === 'series' ? (
                <MeetingSeriesCard
                  key={`series-${item.seriesId}`}
                  item={item}
                  onCardClick={handleCardClick}
                />
              ) : (
                <MeetingSummaryCard
                  key={item.note.entity_id}
                  note={item.note}
                  meeting={item.meeting}
                  onClick={handleCardClick}
                  ownerEntityId={ownerByNote.get(item.note.entity_id)}
                />
              )
            )}
          </div>
        )}
      </div>

      {/* Save current view modal */}
      {currentOrg && savePayload && (
        <SaveCurrentViewModal
          isOpen={saveModalOpen}
          onOpenChange={handleSaveModalOpenChange}
          orgId={currentOrg.id}
          entityType='ahoy_meeting_notes'
          orgName={currentOrg.name}
          payload={savePayload}
          onSaved={onViewSaved}
        />
      )}

      {/* Filter slideout */}
      {currentOrg && (
        <SlideoutMenu isOpen={showFilters} onOpenChange={setShowFilters}>
          <SlideoutMenu.Header onClose={handleCloseFilters}>
            <h2 className='text-lg font-semibold text-primary'>
              Filter meeting summaries
            </h2>
          </SlideoutMenu.Header>
          <FormProvider fields={filterFieldConfig} values={filterFormValues}>
            <SlideoutMenu.Content>
              <SchemaFilterBuilder
                orgId={currentOrg.id}
                entityType='ahoy_meeting_notes'
                groups={builderGroups}
                onChange={handleGroupsChange}
                allowGroups={true}
                onApply={handleApplyFiltersAndClose}
                onAutoApply={handleAutoApplyGroups}
                onValidityChange={setFiltersAreValid}
              />
            </SlideoutMenu.Content>
            <SlideoutMenu.Footer>
              <SubmitButton
                size='md'
                color='primary'
                onClick={handleApplyFiltersClick}
              >
                Apply filters
              </SubmitButton>
            </SlideoutMenu.Footer>
          </FormProvider>
        </SlideoutMenu>
      )}
    </PageLayout>
  );
}
